import { Link, useParams } from "react-router";
import { motion, useScroll, useSpring } from "motion/react";
import { useApi } from "../api.ts";
import { fmtDate } from "../format.ts";
import { Card, EmptyState, Icon, LoadError, Loading, PageHeader } from "../ui/index.tsx";
import { Prose } from "./_shared.tsx";
import { EASE, Stage, Tilt, rise, stagger } from "./member/kit.tsx";
import type { Guide, GuideSummary } from "../../shared/types.ts";

export default function Guides() {
  const guides = useApi<GuideSummary[]>("/guides");

  const groups = new Map<string, GuideSummary[]>();
  for (const g of [...(guides.data ?? [])].sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title))) {
    const key = g.category || "General";
    groups.set(key, [...(groups.get(key) ?? []), g]);
  }

  return (
    <Stage>
      <PageHeader title="Guides" description="Setup help, troubleshooting and how-tos." />
      {guides.loading && !guides.data ? <Loading /> : guides.error && !guides.data ? <LoadError error={guides.error} retry={guides.reload} /> : groups.size === 0 ? (
        <Card padded={false}><EmptyState icon="book" title="No guides yet" body="Check back soon, or open a support ticket." /></Card>
      ) : (
        <div className="space-y-9">
          {[...groups].map(([category, items], gi) => (
            <section key={category}>
              <motion.div className="mb-3 flex items-center gap-3" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, delay: gi * 0.1, ease: EASE }}>
                <h2 className="kicker text-accent">// {category}</h2>
                <motion.span className="h-px flex-1 origin-left bg-gradient-to-r from-line-strong to-transparent" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.15 + gi * 0.1, ease: EASE }} />
              </motion.div>
              <motion.div className="grid gap-3 sm:grid-cols-2" variants={stagger(0.06, gi * 0.1 + 0.1)} initial="hidden" animate="show">
                {items.map((g) => (
                  <Tilt key={g.id} variants={rise} max={3}>
                    <Link to={`/guides/${encodeURIComponent(g.slug)}`}
                      className="pane group flex items-center gap-3 p-4 transition-colors hover:border-accent/40 hover:bg-raised">
                      <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-raised text-muted transition-all group-hover:bg-accent-soft group-hover:text-accent group-hover:shadow-[0_0_18px_-4px_var(--color-accent)]"><Icon name="book" className="size-4" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{g.title}</div>
                        <div className="text-xs text-faint">Updated {fmtDate(g.updated_at)}</div>
                      </div>
                      <Icon name="chevronRight" className="size-4 text-faint transition-all group-hover:translate-x-1 group-hover:text-accent" />
                    </Link>
                  </Tilt>
                ))}
              </motion.div>
            </section>
          ))}
        </div>
      )}
    </Stage>
  );
}

function ReadingProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 24, mass: 0.4 });
  return <motion.div className="fixed inset-x-0 top-0 z-50 h-0.5 origin-left bg-gradient-to-r from-accent to-ember" style={{ scaleX }} aria-hidden />;
}

export function GuideView() {
  const { slug = "" } = useParams();
  const guide = useApi<Guide>(`/guides/${encodeURIComponent(slug)}`);
  const g = guide.data;

  return (
    <Stage>
      <div className="mx-auto max-w-3xl">
        <Link to="/guides" className="group mb-6 inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg">
          <Icon name="chevronLeft" className="size-4 transition-transform group-hover:-translate-x-1" />All guides
        </Link>
        {guide.loading && !g ? <Loading /> : guide.error && !g ? (
          guide.error.status === 404
            ? <EmptyState icon="book" title="Guide not found" action={<Link to="/guides" className="text-sm text-accent hover:underline">Back to guides</Link>} />
            : <LoadError error={guide.error} retry={guide.reload} />
        ) : g && (
          <>
            <ReadingProgress />
            <motion.article variants={stagger(0.1)} initial="hidden" animate="show">
              <motion.div variants={rise} className="kicker text-accent">// {g.category}</motion.div>
              <motion.h1 variants={rise} className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{g.title}</motion.h1>
              <motion.div variants={rise} className="mt-1 text-xs text-faint">Updated {fmtDate(g.updated_at)}</motion.div>
              <motion.div className="mt-6 h-px origin-left bg-gradient-to-r from-accent via-line-strong to-transparent" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.9, delay: 0.3, ease: EASE }} />
              <motion.div variants={rise}><Prose text={g.content} className="mt-8 [&_p]:text-[15px] [&_p]:text-fg/85" /></motion.div>
            </motion.article>
          </>
        )}
      </div>
    </Stage>
  );
}
